import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Salesperson, SalesTeamInterface, SalesTeamService } from '@vendasta/sales';
import { SalespeopleService } from '@vendasta/sales-v2';
import { combineLatest, from, Observable, of } from 'rxjs';
import { catchError, map, mapTo, mergeMap, shareReplay, switchMap, toArray } from 'rxjs/operators';
import { AccessService, Feature, StubAccessService } from '../../../core/access';

interface SalesTeamWithMembers extends SalesTeamInterface {
  members: Salesperson[];
}

@Injectable({
  providedIn: 'root',
})
export class ManageSalespeopleService {
  /**
   * Calls the list Sales Teams endpoint then for each sales team it calls the list members endpoint
   * from there we combine the team and members together and return it
   */
  salesTeams$ = combineLatest([this.partnerId$, this.accessService.hasAccessToFeature(Feature.salesTeam)]).pipe(
    switchMap(([partnerId, hasAccess]) => {
      if (!hasAccess) {
        return of({ results: [] });
      }
      return this.salesTeamService
        .list(partnerId, undefined, undefined, undefined)
        .pipe(catchError(() => of({ results: [] })));
    }),
    mergeMap((resp) =>
      from(resp?.results || []).pipe(
        mergeMap(
          (team: SalesTeamInterface): Observable<SalesTeamWithMembers> =>
            this.getMembersForTeam(team.groupId).pipe(map((m) => ({ ...team, members: m || [] }))),
        ),
        toArray(),
        catchError(() => of([])),
      ),
    ),
    shareReplay(),
  );

  /**
   * Maps the users to point at the teams they are part of. For example
   * { 'a@b.com': [{name: "awsem team", groupId: 'G-12345'}] }
   */
  readonly emailToSalesTeamInfoMap$ = this.salesTeams$.pipe(map(this.emailToSalesTeamInfoMap), shareReplay());

  constructor(
    private readonly api: HttpClient,
    private readonly salesTeamService: SalesTeamService,
    private readonly salespeople: SalespeopleService,
    @Inject('PARTNER_ID') readonly partnerId$: Observable<string>,
    @Inject(StubAccessService) private accessService: AccessService,
  ) {}

  getSalespersonLoginUrl(): Observable<string> {
    return this.api
      .get<{ data: { loginUrl: string } }>('/_ajax/v1/st/login-url', { withCredentials: true })
      .pipe(map((resp) => resp.data.loginUrl));
  }

  resendWelcomeEmail(partnerId: string, salespersonId: string, usePlatformDefaultDomain: boolean): Observable<null> {
    return this.salespeople
      .sendPasswordResetEmail(partnerId, salespersonId, usePlatformDefaultDomain)
      .pipe(mapTo(null));
  }

  deleteSalesperson(salespersonId: string): Observable<string> {
    const body = new HttpParams().set('salesId', salespersonId);

    return this.api
      .post<{ success: string }>('/_ajax/st/delete-account/', body.toString(), {
        headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }),
        withCredentials: true,
      })
      .pipe(map((resp) => resp.success));
  }

  private getMembersForTeam(groupId: string): Observable<Salesperson[]> {
    try {
      return this.salesTeamService.getMembers(groupId);
    } catch (e) {
      return of([]);
    }
  }

  private emailToSalesTeamInfoMap(teams: SalesTeamWithMembers[]): Record<string, { name: string; groupId: string }[]> {
    const spData = {};
    teams.forEach((team) => {
      const teamInfo = {
        name: team.teamName,
        groupId: team.groupId,
      };
      team.members.forEach((member) => {
        if (spData[member.email]) {
          spData[member.email].push(teamInfo);
        } else {
          spData[member.email] = [teamInfo];
        }
      });
    });

    return spData;
  }
}
